import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { Printer, ChevronLeft, X, DownloadCloud } from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || "https://agent100.wibe.si";
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL = 5000;

const STYLE_TITLES = {
  casino_royale: "Klasični smoking",
  action_scene: "Akcijska scena",
  gala_dinner: "Gala večerja",
  female_agent: "Skrivnostna agentka",
  bg_casino: "Casino Monte Carlo",
  bg_aston: "Nočni London",
  bg_rooftop: "Strešna terasa",
  bg_yacht: "Jahta na Rivieri",
};

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(null); // photo object
  const [agent, setAgent] = useState(null); // { online, pending, ... }
  const [printing, setPrinting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [galleryRes, agentRes] = await Promise.all([
        axios.get(`${API}/photobooth/gallery`),
        axios.get(`${API}/print-agent/status`),
      ]);
      setPhotos(galleryRes.data.photos || []);
      setAgent(agentRes.data);
    } catch (err) {
      console.error("Gallery refresh failed:", err);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [refresh]);

  const handlePrint = async () => {
    if (!selected || printing) return;
    setPrinting(true);
    try {
      await axios.post(`${API}/photobooth/print/${selected.id}`);
      toast.success("Fotografija poslana v tisk.", {
        description: agent?.online
          ? "Tiskalnik je povezan — tisk se začne v nekaj sekundah."
          : "Opozorilo: print agent trenutno ni povezan, tisk počaka v vrsti.",
      });
      setSelected(null);
    } catch (err) {
      toast.error("Pošiljanje v tisk ni uspelo.", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="film-grain min-h-screen w-full bg-[#050505] text-white">
      <div className="relative min-h-screen w-full flex flex-col px-4 sm:px-8 pt-3 pb-6">
        {/* Header */}
        <div className="text-center mb-4 fade-up">
          <p className="text-[#A1A1AA] text-[10px] sm:text-xs tracking-[0.4em] uppercase">
            Agent 100
          </p>
          <h1
            className="font-display tracking-bond text-[#C9A961] text-3xl sm:text-4xl md:text-5xl uppercase mt-1"
            data-testid="gallery-title"
          >
            Album misij
          </h1>
          <div className="mx-auto mt-2 w-24 h-px bg-[#C9A961]/40" />
          <p className="mt-2 text-[#A1A1AA] text-sm">
            Izberi fotografijo za prenos ali tisk.
          </p>
          <Link
            to="/prenosi"
            data-testid="gallery-downloads-link"
            className="mt-3 inline-flex items-center justify-center gap-2 border border-[#C9A961]/60 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#050505] transition-all duration-300 px-6 py-2.5 uppercase tracking-bond font-medium text-xs sm:text-sm active:scale-[0.98] rounded-md"
          >
            <DownloadCloud size={16} />
            Prenesi vse slike
          </Link>
          {/* Print agent status */}
          <div
            className="mt-2 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA]"
            data-testid="agent-status"
          >
            <span
              className={`w-2 h-2 rounded-full ${
                agent?.online ? "bg-emerald-500" : "bg-red-500/70"
              }`}
            />
            {agent?.online
              ? `Tiskalnik povezan${agent.pending ? ` · v vrsti: ${agent.pending}` : ""}`
              : "Tiskalnik ni povezan"}
          </div>
        </div>

        {/* Grid */}
        {photos.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[#A1A1AA] tracking-[0.3em] uppercase text-sm">
              Še ni fotografij
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-6xl w-full mx-auto fade-up fade-up-d1"
            data-testid="gallery-grid"
          >
            {photos.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                data-testid={`gallery-photo-${p.id}`}
                className="relative aspect-[3/4] rounded-lg overflow-hidden group border border-white/10 hover:border-[#C9A961]/60 transition-all duration-300"
              >
                <img
                  src={`${API}/photobooth/thumb/${p.id}`}
                  alt={STYLE_TITLES[p.style_id] || "Agent 100"}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-2 text-left">
                  <p className="text-[#C9A961] text-[10px] sm:text-xs tracking-[0.15em] uppercase truncate">
                    {STYLE_TITLES[p.style_id] || p.style_id}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail overlay */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col px-4 sm:px-8 pt-3 pb-6 overflow-y-auto"
          data-testid="gallery-detail"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setSelected(null)}
              data-testid="detail-back-button"
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm tracking-[0.2em] uppercase"
            >
              <ChevronLeft size={18} />
              Nazaj
            </button>
            <p className="font-display text-[#C9A961] tracking-bond uppercase text-sm sm:text-base">
              {STYLE_TITLES[selected.style_id] || selected.style_id}
            </p>
            <div className="w-16" />
          </div>

          {/* Photo — full original image (object-contain), no crop or forced
              aspect ratio, sized as large as the screen allows */}
          <div className="flex justify-center">
            <img
              src={`${API}/photobooth/photo/${selected.id}`}
              alt="Agent 100"
              className="max-w-full object-contain rounded-lg border border-[#C9A961]/40 bg-black shadow-[0_0_60px_rgba(201,169,97,0.15)]"
              style={{ maxHeight: "min(58vh, 1400px)" }}
              data-testid="detail-photo"
            />
          </div>

          {/* QR + actions */}
          <div className="mt-5 flex flex-col items-center gap-5">
            <div className="flex items-center gap-5">
              <div className="bg-white p-3 rounded-md" data-testid="detail-qr">
                <QRCode
                  value={`${PUBLIC_URL}/api/photobooth/share/${selected.id}`}
                  size={150}
                  bgColor="#FFFFFF"
                  fgColor="#050505"
                  level="M"
                />
              </div>
              <p className="text-[#A1A1AA] text-xs sm:text-sm tracking-wider max-w-[220px] text-left">
                Skeniraj QR kodo s telefonom in prenesi svojo fotografijo.
              </p>
            </div>

            <div className="flex gap-3 sm:gap-4">
              <button
                onClick={handlePrint}
                disabled={printing}
                data-testid="detail-print-button"
                className="inline-flex items-center justify-center gap-2 border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10 transition-all duration-300 px-10 sm:px-14 py-5 uppercase tracking-bond font-medium text-sm sm:text-base active:scale-[0.98] disabled:opacity-40"
              >
                <Printer size={20} />
                {printing ? "Pošiljam..." : "Natisni"}
              </button>
              <button
                onClick={() => setSelected(null)}
                data-testid="detail-close-button"
                className="inline-flex items-center justify-center gap-2 border border-white/15 text-white/70 hover:border-white/40 hover:text-white transition-all duration-300 px-8 sm:px-10 py-5 uppercase tracking-bond font-medium text-sm sm:text-base active:scale-[0.98]"
              >
                <X size={20} />
                Zapri
              </button>
            </div>
          </div>
        </div>
      )}

      <Toaster theme="dark" position="top-center" offset="30vh" />
    </div>
  );
}
